import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FileCode2, Boxes, Hash } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listFiles, getFile } from "@/lib/api";
import XmlTreeNode from "@/components/XmlTreeNode";

export default function ReaderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    listFiles().then(setFiles);
  }, []);

  useEffect(() => {
    if (id) getFile(id).then(setDoc).catch(() => setDoc(null));
    else setDoc(null);
  }, [id]);

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-10">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Reader</h1>
          <p className="font-mono text-[11px] text-zinc-500">
            Inspect parsed ADAC structure & assets
          </p>
        </div>
        <Select value={id || ""} onValueChange={(v) => navigate(`/reader/${v}`)}>
          <SelectTrigger
            data-testid="reader-file-select"
            className="w-72 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-sm"
          >
            <SelectValue placeholder="Select a document…" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
            {files.map((f) => (
              <SelectItem key={f.id} value={f.id} className="font-mono text-xs">
                {f.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </header>

      <div className="p-8">
        {!doc ? (
          <div className="grid-texture border border-zinc-800 rounded-sm py-24 flex flex-col items-center text-center">
            <FileCode2 className="w-10 h-10 text-zinc-700 mb-4" />
            <p className="text-sm text-zinc-500">
              Select a document above to read its structure.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <Stat icon={Boxes} label="Total Assets" value={doc.asset_count} />
              <Stat icon={Hash} label="Root Element" value={`<${doc.root_tag}>`} mono />
              <Stat
                icon={FileCode2}
                label="Asset Types"
                value={Object.keys(doc.by_type || {}).length}
              />
            </div>

            <Tabs defaultValue="tree">
              <TabsList className="bg-[#101010] border border-zinc-800 rounded-sm p-1">
                <TabsTrigger value="tree" data-testid="tab-tree" className="rounded-sm font-mono text-xs data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-400">
                  Tree
                </TabsTrigger>
                <TabsTrigger value="assets" data-testid="tab-assets" className="rounded-sm font-mono text-xs data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-400">
                  Assets ({doc.asset_count})
                </TabsTrigger>
                <TabsTrigger value="raw" data-testid="tab-raw" className="rounded-sm font-mono text-xs data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-400">
                  Raw XML
                </TabsTrigger>
              </TabsList>

              <TabsContent value="tree">
                <div className="border border-zinc-800 rounded-sm bg-[#0C0C0C] p-4 overflow-auto" data-testid="tree-view">
                  <XmlTreeNode node={doc.tree} />
                </div>
              </TabsContent>

              <TabsContent value="assets">
                <AssetsTable assets={doc.assets} />
              </TabsContent>

              <TabsContent value="raw">
                <pre
                  data-testid="raw-view"
                  className="border border-zinc-800 rounded-sm bg-[#0C0C0C] p-4 overflow-auto font-code text-xs text-zinc-300 max-h-[70vh] whitespace-pre-wrap"
                >
                  {doc.content}
                </pre>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, mono }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-[#101010] p-4">
      <div className="flex items-center gap-2 text-zinc-500 mb-2">
        <Icon className="w-4 h-4" />
        <span className="font-mono text-[11px] uppercase tracking-wider">{label}</span>
      </div>
      <div className={`text-2xl font-bold text-zinc-100 ${mono ? "font-mono text-lg" : "font-display"}`}>
        {value}
      </div>
    </div>
  );
}

function AssetsTable({ assets }) {
  if (!assets.length)
    return <p className="text-sm text-zinc-500 font-mono">No identifiable assets found.</p>;
  return (
    <div className="border border-zinc-800 rounded-sm overflow-auto max-h-[70vh]" data-testid="assets-table">
      <table className="w-full text-sm">
        <thead className="sticky top-0">
          <tr className="bg-[#101010] border-b border-zinc-800 text-zinc-500 font-mono text-[11px] uppercase tracking-wider">
            <th className="text-left font-medium px-4 py-3">Asset ID</th>
            <th className="text-left font-medium px-4 py-3">Type</th>
            <th className="text-left font-medium px-4 py-3">Attributes</th>
          </tr>
        </thead>
        <tbody>
          {assets.map((a, i) => (
            <tr key={i} className="border-b border-zinc-900 hover:bg-zinc-900/40">
              <td className="px-4 py-3 font-mono text-xs text-blue-400">{a.asset_id}</td>
              <td className="px-4 py-3 font-mono text-xs text-zinc-300">{a.type}</td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(a.fields)
                    .filter(([k]) => k !== "ADACId")
                    .map(([k, v]) => (
                      <span
                        key={k}
                        className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-400"
                      >
                        {k}=<span className="text-zinc-200">{v}</span>
                      </span>
                    ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
